import React, { useState, useEffect } from 'react';
import {
  Card,
  Switch,
  List,
  Typography,
  Tag,
  Space,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Divider,
  Tooltip,
  Alert,
} from 'antd';
import {
  SettingOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchFeatureFlags,
  toggleFeatureFlag,
  createFeatureFlag,
  updateFeatureFlag,
  deleteFeatureFlag,
  initializeDefaultFeatures,
  selectFeatureFlags,
  selectFeatureFlagsLoading,
  selectFeatureFlagsError,
  selectFeatureFlagsInitialized,
  clearError,
} from '../store/slices/featureFlagsSlice';
import type { FeatureFlag, CreateFeatureFlagRequest, UpdateFeatureFlagRequest } from '../lib/featureFlagsApi';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const FeatureFlagsSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const featureFlags = useAppSelector(selectFeatureFlags);
  const loading = useAppSelector(selectFeatureFlagsLoading);
  const error = useAppSelector(selectFeatureFlagsError);
  const initialized = useAppSelector(selectFeatureFlagsInitialized);

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!initialized) {
      dispatch(fetchFeatureFlags());
    }
  }, [dispatch, initialized]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleToggle = async (flag: FeatureFlag) => {
    try {
      await dispatch(toggleFeatureFlag({ 
        id: flag.id, 
        isEnabled: !flag.isEnabled 
      })).unwrap();
      message.success(`${flag.name} ${!flag.isEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      message.error('Failed to toggle feature flag');
    }
  };

  const handleCreate = async (values: CreateFeatureFlagRequest) => {
    try {
      await dispatch(createFeatureFlag(values)).unwrap();
      message.success('Feature flag created successfully');
      setCreateModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Failed to create feature flag');
    }
  };

  const handleEdit = async (values: UpdateFeatureFlagRequest) => {
    if (!editingFlag) return;
    
    try {
      await dispatch(updateFeatureFlag({ 
        id: editingFlag.id, 
        data: values 
      })).unwrap();
      message.success('Feature flag updated successfully');
      setEditModalVisible(false);
      setEditingFlag(null);
      form.resetFields();
    } catch (error) {
      message.error('Failed to update feature flag');
    }
  };

  const handleDelete = (flag: FeatureFlag) => {
    Modal.confirm({
      title: 'Delete Feature Flag',
      content: `Are you sure you want to delete "${flag.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await dispatch(deleteFeatureFlag(flag.id)).unwrap();
          message.success('Feature flag deleted successfully');
        } catch (error) {
          message.error('Failed to delete feature flag');
        }
      },
    });
  };

  const handleInitializeDefaults = async () => {
    try {
      await dispatch(initializeDefaultFeatures()).unwrap();
      await dispatch(fetchFeatureFlags()).unwrap();
      message.success('Default feature flags initialized');
    } catch (error) {
      message.error('Failed to initialize default features');
    }
  };

  const openEditModal = (flag: FeatureFlag) => {
    setEditingFlag(flag);
    form.setFieldsValue({
      key: flag.key,
      name: flag.name,
      description: flag.description,
      category: flag.category,
      version: flag.version,
      isEnabled: flag.isEnabled,
    });
    setEditModalVisible(true);
  };

  const getCategoryColor = (category?: string) => {
    switch (category) {
      case 'ui': return 'blue';
      case 'ai': return 'cyan';
      case 'api': return 'green';
      case 'integration': return 'orange';
      case 'analytics': return 'purple';
      default: return 'default';
    }
  };

  const groupedFlags = featureFlags.reduce((acc, flag) => {
    const category = flag.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(flag);
    return acc;
  }, {} as Record<string, FeatureFlag[]>);

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <Title level={3}>
            <SettingOutlined /> Feature Flags
          </Title>
          <Paragraph type="secondary">
            Control which features are enabled for your organization. Feature flags allow you to 
            toggle functionality without code changes and enable gradual rollouts.
          </Paragraph>
        </div>

        {featureFlags.length === 0 && !loading && (
          <Alert
            message="No Feature Flags Found"
            description="Get started by initializing default feature flags for your organization."
            type="info"
            action={
              <Button type="primary" onClick={handleInitializeDefaults}>
                Initialize Defaults
              </Button>
            }
            style={{ marginBottom: '16px' }}
          />
        )}

        <div style={{ marginBottom: '16px' }}>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              Add Feature Flag
            </Button>
            {featureFlags.length > 0 && (
              <Button onClick={handleInitializeDefaults}>
                Initialize Defaults
              </Button>
            )}
          </Space>
        </div>

        {Object.entries(groupedFlags).map(([category, flags]) => (
          <div key={category} style={{ marginBottom: '24px' }}>
            <Title level={5} style={{ textTransform: 'capitalize', marginBottom: '12px' }}>
              {category} Features
            </Title>
            <List
              dataSource={flags}
              renderItem={(flag) => (
                <List.Item
                  actions={[
                    <Tooltip title="Edit">
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => openEditModal(flag)}
                      />
                    </Tooltip>,
                    <Tooltip title="Delete">
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(flag)}
                      />
                    </Tooltip>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <span>{flag.name}</span>
                        <Tag color={getCategoryColor(flag.category)}>
                          {flag.category || 'other'}
                        </Tag>
                        {flag.version && (
                          <Tag color="default">v{flag.version}</Tag>
                        )}
                      </Space>
                    }
                    description={
                      <div>
                        <Text type="secondary">{flag.description}</Text>
                        <br />
                        <Text code style={{ fontSize: '12px' }}>
                          {flag.key}
                        </Text>
                      </div>
                    }
                  />
                  <Switch
                    checked={flag.isEnabled}
                    onChange={() => handleToggle(flag)}
                    loading={loading}
                  />
                </List.Item>
              )}
            />
            <Divider />
          </div>
        ))}
      </Card>

      {/* Create Feature Flag Modal */}
      <Modal
        title="Create Feature Flag"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            name="key"
            label="Key"
            rules={[
              { required: true, message: 'Please enter feature flag key' },
              { pattern: /^[a-z0-9_]+$/, message: 'Key must contain only lowercase letters, numbers, and underscores' },
            ]}
            extra="Unique identifier for this feature flag (e.g., 'file_management')"
          >
            <Input placeholder="feature_key" />
          </Form.Item>

          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter feature flag name' }]}
          >
            <Input placeholder="Feature Name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="Description of what this feature does"
            />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
          >
            <Select placeholder="Select category">
              <Option value="ui">UI</Option>
              <Option value="ai">AI</Option>
              <Option value="api">API</Option>
              <Option value="integration">Integration</Option>
              <Option value="analytics">Analytics</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="version"
            label="Version"
            initialValue="1.0.0"
          >
            <Input placeholder="1.0.0" />
          </Form.Item>

          <Form.Item
            name="isEnabled"
            label="Enable by default"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setCreateModalVisible(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Feature Flag Modal */}
      <Modal
        title="Edit Feature Flag"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingFlag(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEdit}
        >
          <Form.Item
            name="key"
            label="Key"
            rules={[
              { required: true, message: 'Please enter feature flag key' },
              { pattern: /^[a-z0-9_]+$/, message: 'Key must contain only lowercase letters, numbers, and underscores' },
            ]}
          >
            <Input placeholder="feature_key" />
          </Form.Item>

          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter feature flag name' }]}
          >
            <Input placeholder="Feature Name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="Description of what this feature does"
            />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
          >
            <Select placeholder="Select category">
              <Option value="ui">UI</Option>
              <Option value="ai">AI</Option>
              <Option value="api">API</Option>
              <Option value="integration">Integration</Option>
              <Option value="analytics">Analytics</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="version"
            label="Version"
          >
            <Input placeholder="1.0.0" />
          </Form.Item>

          <Form.Item
            name="isEnabled"
            label="Enabled"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setEditModalVisible(false);
                setEditingFlag(null);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FeatureFlagsSettings;